import { cva, type VariantProps } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";

export const buttonVariants = cva(
  [
    "native:flex native:flex-row web:transition-colors group relative inline-flex items-center justify-center overflow-hidden outline-none disabled:cursor-not-allowed",
    // Keyboard focus on web: the ring Chip, Checkbox, Switch and RadioButton
    // draw. `web:` because css-interop drops `:focus-visible` and would apply
    // the ring at rest on native. Figma's `focus-ring-width` and
    // `focus-ring-offset` (1.5 and 2).
    "web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus",
    // Touch screens on web: a tap area of at least 44px (native: `buttonHitSlop`),
    // which the root's own clip would otherwise cut off
    minTouchTargetClassName,
    "[@media(pointer:coarse)]:overflow-visible",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-luce-button-cta-default border-0",
          "hover:bg-luce-button-cta-hover",
          "active:bg-luce-button-cta-pressed",
          "disabled:bg-luce-button-disabled-ctaDisabled",
        ].join(" "),
        secondary: [
          "border-luce-button-cta2-border border border-solid bg-transparent",
          "hover:border-luce-button-cta2-borderHover hover:bg-luce-button-cta2-surfaceHover",
          "active:border-luce-button-cta2-borderHover active:bg-luce-button-cta2-surfacePressed",
          "disabled:border-luce-button-disabled-ctaBorderDisabled disabled:bg-transparent",
        ].join(" "),
        tertiary: [
          "border-0 bg-transparent",
          "hover:bg-luce-button-cta2-surfaceHover",
          "active:bg-luce-button-cta2-surfacePressed",
          "disabled:bg-transparent",
        ].join(" "),
        neutral: [
          "border-luce-neutral-border-mediumEmphasis bg-luce-neutral-background-default border border-solid",
          "hover:bg-luce-neutral-background-intermediate",
          "active:bg-luce-neutral-background-attenuated",
          "disabled:border-luce-button-disabled-ctaBorderDisabled disabled:bg-luce-neutral-background-default",
        ].join(" "),
        destructive: [
          "bg-luce-button-destructive-default border-0",
          "hover:bg-luce-button-destructive-hover",
          "active:bg-luce-button-destructive-pressed",
          "disabled:bg-luce-button-disabled-ctaDisabled",
        ].join(" "),
        "destructive-secondary": [
          "border-luce-button-destructive-border border border-solid bg-transparent",
          "hover:border-luce-button-destructive-borderHover hover:bg-luce-button-destructive-surface",
          "active:border-luce-button-destructive-borderHover active:bg-luce-button-destructive-surfacePressed",
          "disabled:border-luce-button-disabled-ctaBorderDisabled disabled:bg-transparent",
        ].join(" "),
        success: [
          "bg-luce-system-success-default border-0",
          "hover:bg-luce-system-success-hover",
          "active:bg-luce-system-success-pressed",
          "disabled:bg-luce-button-disabled-ctaDisabled",
        ].join(" "),
        // Pressed keeps the hover border, like the other outline variants and
        // Figma's Icon-Button set (5998:54458)
        "success-secondary": [
          "border-luce-button-success-border border border-solid bg-transparent",
          "hover:border-luce-button-success-borderHover hover:bg-luce-button-success-surface",
          "active:border-luce-button-success-borderHover active:bg-luce-button-success-surfacePressed",
          "disabled:border-luce-button-disabled-ctaBorderDisabled disabled:bg-transparent",
        ].join(" "),
        // IconButton's only (Button's type doesn't offer it): no fill, on a
        // brand-coloured bar such as NavHeader's (molecules audit DQ8). Figma
        // draws no hover or pressed look; both take the bar's hover colour
        "on-brand": [
          "border-0 bg-transparent",
          "hover:bg-luce-brand-staticHover",
          "active:bg-luce-brand-staticHover",
          "disabled:bg-transparent",
        ].join(" "),
      },
      // Heights are in the compounds below
      size: {
        sm: "gap-spacing-xs",
        md: "gap-spacing-sm",
        lg: "gap-spacing-sm",
      },
      iconOnly: {
        // Both bind Figma's radius variable (split rows 23 and 33)
        true: "rounded-radius-full p-spacing-0",
        // A labelled button is never wider than its container and can shrink in
        // a row, so a long label ends in "…" instead of overflowing. Without
        // `min-w-0` a web flex item can't shrink below its label once the root
        // stops clipping (touch screens, FabButton).
        false: "rounded-radius-full min-w-0 max-w-full",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
      loading: {
        true: "cursor-wait",
        false: "",
      },
    },
    compoundVariants: [
      // IconButton: a square of Figma's `_cta` icon line plus its padding
      // (`getCtaBoxSize`), 24/32/40, desktop 32/40/48
      { iconOnly: true, size: "sm", class: "h-cta-sm w-cta-sm md:h-cta-sm-dt md:w-cta-sm-dt" },
      { iconOnly: true, size: "md", class: "h-cta-md w-cta-md md:h-cta-md-dt md:w-cta-md-dt" },
      { iconOnly: true, size: "lg", class: "h-cta-lg w-cta-lg md:h-cta-lg-dt md:w-cta-lg-dt" },
      // IconButton's neutral disabled fill is Figma's CTA-disabled (split row 27).
      // A labelled neutral Button stays white: no row changes it
      { iconOnly: true, variant: "neutral", class: "disabled:bg-luce-button-disabled-ctaDisabled" },
      // Button: a fixed height, not vertical padding, so it stays the same
      // whatever the content (e.g. text → spinner). The heights are Figma's
      // `_button/height/*`; the desktop padding binds the spacing class with
      // Figma's desktop value (token round 2, DS-002 "Control sizes")
      {
        iconOnly: false,
        size: "sm",
        class: "px-spacing-md h-button-sm md:h-button-sm-dt md:px-spacing-lg",
      },
      {
        iconOnly: false,
        size: "md",
        class: "px-spacing-lg h-button-md md:h-button-md-dt md:px-spacing-xl",
      },
      {
        iconOnly: false,
        size: "lg",
        class: "px-spacing-lg h-button-lg md:h-button-lg-dt md:px-spacing-xl",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      iconOnly: false,
      fullWidth: false,
      loading: false,
    },
  },
);

export type ButtonVariantsProps = VariantProps<typeof buttonVariants>;
